import { isLaunchByHarmonyConnectApp } from 'system/SystemUtil';
import { REGEX_URL } from 'utils/Regexes';

const CLEAR_HISTORY_AFTER_NAVIGATION_KEY = 'harmony.clearHistoryAfterNavigation';

/**
 * Check if GCW needs to apply customization for Harmony Connect App, e.g.
 *   1. Hide the page header (App would implement its native functions)
 *   2. Hide the nav sidebar (App would implement its native nav menu)
 * @returns {boolean}
 */
export const isHarmonyConnect = () => {
    return isLaunchByHarmonyConnectApp();
};

/**
 * Set nav header in Harmony Connect App
 * @param {string} headerName
 */
export const updateHarmonyNavHeader = (headerName) => {
    if (!isHarmonyConnect()) {
        return;
    }
    window?.harmonyConnectObj?.handle?.('changeTitle', { title: headerName });
};

/**
 * Navigates to the give path in Harmony connect
 * @param path full url or only the pathname.
 */
export const handleNavigateInHarmonyConnect = (path) => {
    const url = REGEX_URL.test(path) ? path : `${window.location.origin}${path}`;
    window?.harmonyConnectObj?.handle?.('openPage', { url });
};

/**
 * Go to home page in Harmony Connect App
 */
export const goToHarmonyConnectHomePage = () => {
    // In Harmony connect, navigate to the home page
    window?.harmonyConnectObj?.handle?.('navigationToHome');
};

/**
 * Disable harmony pull refresh
 * @param {boolean} disable
 */
export const disableHarmonyPullRefresh = (disable = false) => {
    if (!isHarmonyConnect()) {
        return;
    }
    window?.harmonyConnectObj?.handle?.('disableRefresh', { disable });
};

/**
 * Get Harmony Connect App theme
 * @returns {Promise<string>}
 */
export const getHarmonySystemTheme = async () => {
    return window.LAUNCH_BY_HARMONY_CONNECT_APP?.appTheme?.();
};

/**
 * clear url history in Harmony Connect App
 * @returns {void}
 */
export const clearHistoryInHarmonyConnect = () => {
    if (!isHarmonyConnect()) {
        return;
    }
    window?.harmonyConnectObj?.handle?.('clearHistory');
};

/**
 * Mark that the history should be cleared after navigation in Harmony Connect App.
 * @returns {void}
 */
export const markClearHistoryAfterNavigation = () => {
    if (!isHarmonyConnect()) {
        return;
    }

    sessionStorage.setItem(
        CLEAR_HISTORY_AFTER_NAVIGATION_KEY,
        JSON.stringify({
            sourceUrl: window.location.href
        })
    );
};

/**
 * Consume the pending clear history action after navigation in Harmony Connect App.
 * @returns {void}
 */
export const consumeClearHistoryAfterNavigation = () => {
    const pendingValue = sessionStorage.getItem(CLEAR_HISTORY_AFTER_NAVIGATION_KEY);
    if (!pendingValue) {
        return;
    }

    try {
        const { sourceUrl } = JSON.parse(pendingValue);

        if (sourceUrl === window.location.href) {
            return;
        }
        sessionStorage.removeItem(CLEAR_HISTORY_AFTER_NAVIGATION_KEY);
        clearHistoryInHarmonyConnect();
    } catch {
        sessionStorage.removeItem(CLEAR_HISTORY_AFTER_NAVIGATION_KEY);
    }
};

/**
 * Notify Harmony Connect App that the token has expired
 * @param {string} url The URL that has expired token.
 */
export const notifyTokenExpired = (url) => {
    window?.harmonyConnectObj?.handle?.('tokenExpired', url);
};
